import { useState, useRef, useEffect } from "react";
import styles from "./AIAssessments.module.css";
import Message from "./MessageV1";
import Header from "./Header";
import InterestsPop from "./MyFeeds/Interests";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllInterestsAction,
  getUserInterestsAction,
  getUserSubInterestsAction,
} from "../actions/profileActions";
import { Alert, Box, Snackbar } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
} from "chart.js";
import { Radar, Bar } from "react-chartjs-2";
import {
  AddOutlined,
  ArrowLeft,
  ArrowUpwardOutlined,
  AutoAwesomeOutlined,
  ArrowForwardIosOutlined,
  CloseOutlined,
  DeleteOutline,
  DescriptionOutlined,
  VisibilityOutlined
} from "@mui/icons-material";
import { Loading } from "react-admin";
import fetchItemsNextToken from "../../graphql/fetchItemsNextToken";
import {
  listTblGradeMasters,
  searchCardItems,
  searchCards,
} from "../../graphql/queries";
import fetchSearchItemsNextToken from "../../graphql/fetchSearchItemsNextToken";
import Loader2 from "./Loader2";
import CustomModal from "./utils/CustomModal/CustomModal";
import {
  createAIChatSession,
  generateQuestion,
  fetchAIChatBlocks,
  helperPrompt,
  listAIChatSessions,
  listXPs,
  userAttempt,
  deleteAIChatSession,
} from "../../services/aiChatService";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { deleteAssessment, listAssessment } from "../../services/assessmentService";
import { listPdfAssessment } from "../../services/pdfService";
import * as queries from "../../graphql/queries";
import { AIChatTypes, QuestionTypes } from "../auth/util/CustomConstants";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  PointElement
);

/**
 * @component AISkillJourney
 * @description An AI-powered tutoring interface that helps users learn through interactive sessions.
 * Features include:
 * - Question-based learning with MCQ format
 * - AI chat assistant to help with questions
 * - XP and level progression system
 * - Learning path customization by topic, grade, language, board, and region
 * - Progress visualization with radar charts
 * - Responsive design with adaptive layouts for desktop and mobile
 *
 * The component manages:
 * - Chat history and message threading
 * - Question generation and answer evaluation
 * - XP rewards and penalties
 * - User profile integration
 * - Learning session persistence
 *
 * @returns {JSX.Element} The AISkillJourney component
 */
const AIAssessments = () => {
  const { id: pdfId } = useParams() || null;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const profile = useSelector((state) => state.profileReducer); // profile data

  // user
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("");

  // pdf assessment
  const [selectedPdfAssessment, setSelectedPdfAssessment] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [numberOfQuestions, setNumberOfQuestions] = useState(10);
  const [totalMarks, setTotalMarks] = useState(100);
  const [passingMarks, setPassingMarks] = useState(40);
  const [assessmentType, setAssessmentType] = useState("MCQ");
  const [assessmentName, setAssessmentName] = useState("");
  const [assessmentDescription, setAssessmentDescription] = useState("");
  const [assessmentDuration, setAssessmentDuration] = useState(60);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfAssessmentQuestions, setPdfAssessmentQuestions] = useState([]);
  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [aiAssessmentChat, setAiAssessmentChat] = useState(null);
  const [aiAssessmentChatBlocks, setAiAssessmentChatBlocks] = useState([]);
  const [activeAssessmentMarks, setActiveAssessmentMarks] = useState(null);
  const [viewAssessmentQuestionsModal, setViewAssessmentQuestionsModal] = useState(false);
  const [selectedAssessmentForModal, setSelectedAssessmentForModal] = useState(null);

  const setOpenAssessmentEditModal = () => {
    setViewAssessmentQuestionsModal(false)
  }

  // Bar Graph
  const barGraphLabels = [`${firstName} ${lastName}`];
  const barGraphData = {
    labels: barGraphLabels,
    datasets: [
      {
        label: 'Assessment Users',
        // data: barGraphLabels.map(() => Math.floor(Math.random() * 101)),
        data: [activeAssessmentMarks],
        backgroundColor: "rgba(53, 162, 235, 0.5)",
      },
    ],
  };
  const barGraphOptions = {
    responsive: true,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Assessment Users',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Marks Obtained',
        },
        beginAtZero: true,
        ticks: {
          stepSize: 5,
        },
        max: 100, // this is total marks
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Assessment Results',
      },
    },
  };

  const [showBarGraph, setShowBarGraph] = useState(false);
  const [barGraphLabelsState, setBarGraphLabelsState] = useState(barGraphLabels);
  const [barGraphDataState, setBarGraphDataState] = useState(barGraphData);
  const [barGraphOptionsState, setBarGraphOptionsState] = useState(barGraphOptions);
  

  //loaders
  const [showLoader, setShowLoader] = useState(false); //main loader
  const [sidePanelLoader, setSidePanelLoader] = useState(false);
  const [questionPanelLoader, setQuestionPanelLoader] = useState(false);
  const [generatingResponse, setGeneratingResponse] = useState(false);

  //snackbar
  const [openToast, setOpenToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [severityClass, setSeverityClass] = useState("success");

  //helper AI
  const [userPrompt, setUserPrompt] = useState("");
  const [messages, setMessages] = useState([]);
  const [aiMode, setAIMode] = useState(true);
  const [chats, setChats] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [pdfAssessments, setPdfAssessments] = useState([]);
  const [tmpMessages, setTmpMessages] = useState([]); // for switching ai mode chats
  const [containerScrollDown, setContainerScrollDown] = useState("");
  const historyRef = useRef(null);
  const scrollElemRef = useRef(null);

  //question panel
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [latestQuestionIndex, setLatestQuestionIndex] = useState(0);
  const [options, setOptions] = useState([]);
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [disableOptions, setDisableOptions] = useState(false);
  const [showNextQuestionBtn, setShowNextQuestionBtn] = useState(false);

  //XP system
  const [remainingXP, setRemainingXP] = useState(10);
  const [attemptCount, setAttemptCount] = useState(0);

  //configure new AI chat
  const [openConfigurationModal, setOpenConfigurationModal] = useState(false);
  //openAssessmentGenerateModal
  const [openAssessmentGenerateModal, setOpenAssessmentGenerateModal] = useState(false);

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [language, setLanguage] = useState("1");
  const [board, setBoard] = useState("1");
  const [region, setRegion] = useState("0");
  const [currentGrade, setCurrentGrade] = useState(
    "09fe4dbf-446c-4207-a37c-99f427b05b64"
  );
  const [gradeList, setGradeList] = useState([]);
  const [boardList, setBoardList] = useState([
    { id: 1, name: "General" },
    { id: 2, name: "CBSE" },
    { id: 3, name: "ICSE" },
    { id: 4, name: "State" },
  ]);
  const [regionList, setRegionList] = useState([
    // only for State Board and some countries
    { id: 0, name: "Global" },
    { id: 1, name: "Maharashtra" },
    { id: 2, name: "Tamil Nadu" },
    { id: 3, name: "Gujarat" },
    { id: 4, name: "Delhi" },
    { id: 5, name: "Assam" },
    { id: 6, name: "Meghalaya" },
    { id: 7, name: "United States of America" },
    { id: 8, name: "United Kingdom" },
    { id: 9, name: "Canada" },
    { id: 10, name: "Australia" },
    { id: 11, name: "Germany" },
    { id: 12, name: "France" },
    { id: 13, name: "Japan" },
    { id: 14, name: "China" },
    { id: 15, name: "Russia" },
    { id: 16, name: "Brazil" },
    { id: 17, name: "South Africa" },
  ]);
  const [languageList, setLanguageList] = useState([
    { id: 1, name: "English" },
    { id: 2, name: "Hindi" },
    { id: 3, name: "Marathi" },
    { id: 4, name: "Gujarati" },
    { id: 4, name: "Tamil" },
    { id: 5, name: "Khasi" },
    { id: 6, name: "Garo" },
    { id: 7, name: "Chinese" },
    { id: 8, name: "Spanish" },
    { id: 9, name: "French" },
    { id: 10, name: "German" },
    { id: 11, name: "Japanese" },
  ]);

  //radar chart
  const [data, setData] = useState({ labels: [], datasets: [] });
  const chartRef = useRef(null);
  const chartWrapperRef = useRef(null);
  const [chartDimensions, setChartDimensions] = useState({
    width: window.innerWidth <= 600 ? 90 : window.innerWidth <= 800 ? 50 : 25,
  });

  //panel view controller
  const [sidePanel, setSidePanel] = useState(
    window.innerWidth <= 800 ? false : true
  );
  const [chartPanel, setChartPanel] = useState(false);
  const [chatPanel, setChatPanel] = useState(false); // only for mobile view
  const [showPopup, setShowPopup] = useState(false);

  //ui related
  const animationFrameId = useRef(null);

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const handleCloseConfigurationModal = () => {
    setOpenConfigurationModal(false);
    setSelectedTopic(null);
    setLanguage("1");
    setBoard("1");
    setRegion("0");
    setCurrentGrade("09fe4dbf-446c-4207-a37c-99f427b05b64");
  };

  const handleCloseGenerateModal = () => {
    setOpenAssessmentGenerateModal(false);
    setSelectedFile(null);  
    setPrompt("");
    setNumberOfQuestions(10);
    setTotalMarks(100);
    setPassingMarks(40);
    setAssessmentType("MCQ");
    setAssessmentName("");
    setAssessmentDescription("");
    setAssessmentDuration(60);
  };

  const handleGenerateAssessment = async () =>{
    console.log("Generating Assessment from PDF")
  }

  /**
   * Sets up question data for a quiz or assessment.
   *
   * @param {Object} data - The data object containing question information
   * @param {Object} data.content - The content of the question
   * @param {string} data.content.question - The text of the question
   * @param {Array} data.content.options - Available options for the question
   * @param {number} data.content.correctIndex - Index of the correct option
   * @param {number} data.questionIndex - The index or position of the question in the quiz
   *
   * @returns {void} This function doesn't return a value
   */
  const setQuestionData = (data) => {
    const questionData = data.content;
    setAttemptCount(0);
    setRemainingXP(10);
    setShowNextQuestionBtn(false);
    setQuestionIndex(data.questionIndex);
    setDisableOptions(false);
    setSelectedOption("");
    setMessages([]);
    setCurrentQuestion(questionData.question);
    setOptions(questionData.options);
    setCorrectAnswer(questionData.correctIndex);

    if (latestQuestionIndex < data.questionIndex) {
      setLatestQuestionIndex(data.questionIndex);
    }
  };

  useEffect(() => {
    if(selectedPdfAssessment && selectedPdfAssessment.aiCardItemid) {
      console.log("Selected PDF Assessment", selectedPdfAssessment);
      createPdfAssessmentAIChat(selectedPdfAssessment);
    }
  },[selectedPdfAssessment])


  useEffect(() => {
    if(aiAssessmentChat !== null && aiAssessmentChat !== undefined && aiAssessmentChat.id) {
      getPdfAssessmentAIChatBlocks();
    }
  },[aiAssessmentChat])

  // Checking
  useEffect(() => {
    console.log("AI Assessment Chat Blocks: ", aiAssessmentChatBlocks); 
  },[aiAssessmentChatBlocks]) 
  
  // Not Triggered
  useEffect(() => {
      if(selectedPdfAssessment && selectedPdfAssessment.questions.length === aiAssessmentChatBlocks.length) {
      
      let totalMarksObtained = aiAssessmentChatBlocks.reduce((acc, block) => {
        const content = JSON.parse(block.content);
        if (content && typeof content.xpEarned === 'number') {
          return acc + content.xpEarned;
        }
        return acc;
      }, 0); 
      console.log("Total Marks Obtained: ", totalMarksObtained);
      let totalMarks = selectedPdfAssessment.totalMarks;
      console.log("Total Marks: ", totalMarks);
      
      
      setBarGraphLabelsState([`${firstName} ${lastName}`]);
      setBarGraphDataState({
        labels: [`${firstName} ${lastName}`],
        datasets: [
          {
            label: "Marks Obtained",
            data: [totalMarksObtained],
            backgroundColor: "rgba(53, 162, 235, 0.5)",
          },
        ],
      });
      setBarGraphOptionsState({
        responsive: true,
        scales: {
          x: {
            title: {
              display: true,
              text: "Assessment Users",
            },
          },
          y: {
            title: {
              display: true,
              text: "Marks Obtained",
            },
            beginAtZero: true,
            ticks: {
              stepSize: 5,
            },
            max: totalMarks, // this is total marks
          },
        },
        plugins: {
          legend: {
            position: "top",
          },
          title: {
            display: true,
            text: `Assessment Results(Out of ${totalMarks})`,
          },
        },
      });
      setShowBarGraph(true);
    } 
    
  }, [aiAssessmentChatBlocks]);

  useEffect(() => {
    console.log("QuestionIndex Changed: ", questionIndex);
    if (!selectedPdfAssessment) {
      return;
    }

    if(questionIndex !== 0 && questionIndex < selectedPdfAssessment.questions.length) {
      const questionData = selectedPdfAssessment.questions;
      setCurrentQuestion(questionData[questionIndex].question);
      setOptions(questionData[questionIndex].options);
      setCorrectAnswer(questionData[questionIndex].correct_answer)    
    } 

    if(questionIndex === selectedPdfAssessment.questions.length) {
      setShowNextQuestionBtn(false);
      setDisableOptions(true);
    }
  }, [questionIndex])

  useEffect(() => {
    if(activeAssessmentMarks) {
      console.log("Active Assessment Marks Changed: ", activeAssessmentMarks);
      // if (activeAssessmentMarks !== null && activeAssessmentMarks !== undefined) {
      //   setRemainingXP((prev) => prev - activeAssessmentMarks);
      //   console.log("Remaining XP: ", remainingXP);
      // }
      setBarGraphDataState(barGraphData)
    }
  },[activeAssessmentMarks])

  const setAssessmentQuestionData = () => {
    if (!selectedPdfAssessment) return;

    const questionData = selectedPdfAssessment.questions;
    setAttemptCount(0);
    setRemainingXP(10);
    setShowNextQuestionBtn(false);
    setDisableOptions(false);
    setSelectedOption("");
    setMessages([]);
    setCurrentQuestion(questionData[0].question);
    setOptions(questionData[0].options);
    setCorrectAnswer(questionData[0].correct_answer)
    setActiveAssessmentMarks(0);
  }

  const createPdfAssessmentAIChat = async (pdfAssessment) => {
    try {
      const data = {
        type: AIChatTypes.PDF_ASSESSMENT,
        config: {
          name: pdfAssessment.name,
          description: pdfAssessment.description,
          questionType: QuestionTypes.SINGLE_CHOICE_MCQ,
          numberOfQuestions: pdfAssessment.questions.length,
          totalMarks: pdfAssessment.totalMarks,
          passingMarks: pdfAssessment.passingMarks,
          pdfId: pdfAssessment.pdfId,
          id: pdfAssessment.aiCardItemid
        },
        aiMode: "general"
      };

      const aiChatCreateResponse = await createAIChatSession(data);
      console.log("AI Chat Created: ", aiChatCreateResponse);

      if(aiChatCreateResponse?.status === 200) {
        setAiAssessmentChat(aiChatCreateResponse?.data?.data.aiChat);
      } else {
        showToast("Error in creating AI chat session for assessment", "error");
        return;
      }
      // else if (aiChatCreateResponse?.status === 400) {
      //   setCurrentQuestion("You have already taken this assessment");
      //   // show this as toast and also at the question panel
      // }
    } catch (error) {
      console.error("Error in (createAssessmentAIChat): ", error);
      showToast("Error in creating assessment AI chat", "error");
    }
  }

  const getPdfAssessmentAIChatBlocks = async () => {
    try {
      console.log("Fetching AI Assessment Chat Blocks");
      if (!aiAssessmentChat || !aiAssessmentChat.id) {
        console.error("AI Assessment Chat is not available");
        return;
      }

      setQuestionPanelLoader(true);
      const response = await fetchAIChatBlocks(aiAssessmentChat.id, null);
      if (response?.status !== 200) {
        throw Error("Error in fetching AI chat blocks");
      }

      const blocks = response?.data?.data?.block;
      console.log("AI Chat Blocks: ", blocks);

      if(showLoader) {
        setShowLoader(false);
      }

      if (blocks && Array.isArray(blocks) && blocks.length === 0) {
        console.log("AI Chat Blocks are empty");
        // setIsAssessmentAlreadyTaken(false);
        setShowBarGraph(false);
        setAssessmentQuestionData();
      }

      if(blocks.length === selectedPdfAssessment.questions.length) {
        console.log("Assessment already completed, showing graph");
        setShowNextQuestionBtn(false);
        setCurrentQuestion("You have already completed the assessment. Check Bar graph for results");
        setOptions([]);

        let totalMarksObtained = blocks.reduce((acc, block) => {
          const content = block.content;
          if (content && typeof content.xpEarned === 'number') {
            return acc + content.xpEarned;
          }
          return acc;
        }, 0); 
        console.log("Total Marks Obtained: ", totalMarksObtained);
        let totalMarks = selectedPdfAssessment.totalMarks;
        console.log("Total Marks: ", totalMarks);

        setBarGraphLabelsState(["Bhaskar Chetty"])
        setBarGraphDataState({
          labels: [`${firstName} ${lastName}`],
          datasets: [
            {
              label: "Marks Obtained",
              data: [totalMarksObtained],
              backgroundColor: "rgba(53, 162, 235, 0.5)",
            },
          ],
        });
        setBarGraphOptionsState({
          responsive: true,
          scales: {
            x: {
              title: {
                display: true,
                text: "Assessment Users",
              },
            },
            y: {
              title: {
                display: true,
                text: "Marks Obtained",
              },
              beginAtZero: true,
              ticks: {
                stepSize: 5,
              },
              max: totalMarks, // this is total marks
            },
          },
          plugins: {
            legend: {
              position: "top",
            },
            title: {
              display: true,
              text: `Assessment Results(Out of ${totalMarks})`,
            },
          },
        });

        setShowBarGraph(true);
      } else {
        console.log("Assessment not complete");

        let totalMarksObtainedTillLastQuestion = blocks.reduce((acc, block) => {
          const content = block.content;
          if (content && typeof content.xpEarned === 'number') {
            return acc + content.xpEarned;
          }
          return acc;
        }, 0);
        setActiveAssessmentMarks(totalMarksObtainedTillLastQuestion);
        setQuestionIndex(blocks.length);
        if(showLoader) {
          setShowLoader(false);
        }
      }
    } catch (error) {
      console.error("Error in (getPdfAssessmentAIChatBlocks): ", error);
      showToast("Error in fetching question data", "error");
    } finally {
      setQuestionPanelLoader(false);
    }
  }

  /**
   * Fetches a new question from the AI service.
   *
   * This async function attempts to generate a new question using the current AI chat ID.
   * It handles the UI state during the fetch operation by showing a loader while in progress.
   * Upon success, it updates the question data state with the generated question.
   * If an error occurs, it displays an error toast notification to the user.
   *
   * @async
   * @function fetchQuestion
   * @returns {Promise<void>} A promise that resolves when the question fetch operation completes
   * @throws {Error} If there's an issue generating the question or if no question is found
   */
  const fetchQuestion = async () => {
    try {
      // update current question index
      // upate current question data
      // show arrowleft icon if current question index is greater than 0
      // display the next question with options
      
      // Enabling this will show "Next Question" button as soon as the question is fetched and rendered which is not desired in case of Assessments
      // setShowNextQuestionBtn(false);

      if(!isOptionSelected) {
        // Dont allow user to proceed to next question if no option is selected
        showToast("Please select an option before proceeding to Next Question", "warning");
        return;
      } else {
        // set to false since option was selected
        // and user is proceeding to next question
        setIsOptionSelected(false);
      }

      // Dont show "Next Question" button as soon as the question is fetched and rendered.
      if(questionIndex+1 === selectedPdfAssessment.questions.length) {
        setShowNextQuestionBtn(false);
        setCurrentQuestion("You have completed the assessment");
        setOptions([]);
        setShowBarGraph(true);
        return;
      }


      setQuestionIndex((prev) => prev + 1);
      setQuestionPanelLoader(true);
      setSelectedOption("");
      setDisableOptions(false);
      console.log("Question Index: ", questionIndex);
      console.log("Seelected Option: ", selectedOption);
      console.log("Marks till Now: ", activeAssessmentMarks);
    } catch (error) {
      console.error("Error in (fetchQuestion): ", error);
      setQuestionIndex((prev) => prev - 1);
      showToast("Error in fetching next Question", "error");
    } finally {
      setQuestionPanelLoader(false);
    }
  };

  /**
   * Handles the submission of a user prompt to get an AI helper response.
   *
   * @async
   * @param {Event} e - The form submission event
   * @returns {Promise<void>}
   * @throws {Error} When there is an error communicating with the AI service
   *
   * @description
   * This function:
   * 1. Prevents the default form submission behavior
   * 2. Validates and trims the user prompt
   * 3. Sets the UI to a "generating" state
   * 4. Sends the prompt to the backend AI service
   * 5. Processes the AI response, including any XP penalties
   * 6. Updates the messages state with both user prompt and AI response
   * 7. Handles errors and displays appropriate toast notifications
   * 8. Resets the UI state regardless of success or failure
   */
  const fetchHelperResponse = async (e) => {
    try {
      setOpenToast(false);
      e.preventDefault();
      const _userPrompt = userPrompt.trim();
      if (_userPrompt !== "") {
        setGeneratingResponse(true);
        const data = { userPrompt: _userPrompt };
        setMessages((prev) => [
          ...prev,
          { role: "user", content: _userPrompt },
        ]);
        setUserPrompt("");

        const response = await helperPrompt(pdfId, data);
        if (response?.status !== 200) {
          setToastMsg("Error in sending message");
          setSeverityClass("error");
          setOpenToast(true);
          return;
        }

        const blocks = response?.data?.data?.block;
        const generatedMessage = blocks.find(
          (block) => block.actor === "system"
        );

        const penaltyAmount = generatedMessage?.content?.xpPenalty;
        if (penaltyAmount) {
          setRemainingXP((prev) => prev - penaltyAmount);
          showToast(`Penalty applied: ${penaltyAmount} XP`, "error");
        }

        setGeneratingResponse(false);
        setMessages((prev) => [
          ...prev,
          { role: "model", content: generatedMessage.content.content },
        ]);
      }
    } catch (error) {
      console.error("Error in (fetchHelperResponse): ", error);
      showToast("Our AI is busy, please try again", "error");
    } finally {
      setGeneratingResponse(false);
    }
  };

  /**
   * Fetches all XP (Experience Points) data from the API and processes it for display.
   *
   * @async
   * @function fetchAllXPData
   * @returns {Promise<Object|null>} An object containing the processed XP data organized by topic, or null if an error occurs.
   * @description
   * This function:
   * 1. Shows a loading indicator in the side panel
   * 2. Calls the listXPs API to retrieve XP details
   * 3. Processes the API response to create a formatted data object
   * 4. Sets up data for chart visualization with labels and datasets
   * 5. Handles errors by displaying appropriate toast messages
   * 6. Hides the loading indicator when complete
   */
  const fetchAllXPData = async () => {
    try {
      setSidePanelLoader(true);
      let xpDetails = await listXPs();
      if (xpDetails?.status !== 200) {
        throw Error("Error in fetching XP details");
      }

      let chartData = xpDetails?.data?.data?.xpDetails;
      if (!chartData || chartData.length === 0) return null;

      return chartData; 
    } catch (error) {
      console.error("Error in (fetchAllXPData): ", error);
      showToast("Can't get XP details", "error");
      return null;
    } finally {
      setSidePanelLoader(false);
    }
  };

  const updateChartData = (data) => {
    let xpData = {};
      let labels = [];
      let _data = [];
      data.forEach((item) => {
        xpData[item.topic] = {
          id: item.id,
          name: item.topic,
          level: item.level,
          xp: item.totalXP,
          xpToNextLevel: item.xpToReachNextLevel,
          questionSolved: item.questionCount,
          xpInLevel: item.xpInLevel,
        };
        labels.push(item.topic);
        _data.push(item.totalXP);
      });

      setData({
        labels: labels,
        datasets: [
          {
            label: "XP",
            data: _data,
            backgroundColor: "rgba(140, 199, 59, 0.2)",
            borderColor: "rgba(140, 199, 59, 1)",
            borderWidth: 1,
            pointBackgroundColor: "rgba(140, 199, 59, 1)",
            fill: false,
          },
        ],
      });
  }

  /**
   * Calculates progress and statistics for a user's current quiz attempt.
   *
   * @param {Array} attemptDetails - Array containing details of each attempt made by the user.
   * @param {Array} helperInteractions - Array containing details of any helper interactions used during the attempt.
   * @returns {null} Returns null if there's an error or if attemptDetails is empty.
   *
   * @description
   * This function processes attempt details and helper interactions to:
   * - Track the total number of attempts
   * - Calculate XP penalties
   * - Determine if the question was solved correctly
   * - Update UI state based on attempt results (selected option, next button visibility)
   * - Updates attempt count and remaining XP in the component state
   */
  const calculateCurrentAttemptProgress = (
    attemptDetails,
    helperInteractions
  ) => {
    try {
      if (!attemptDetails || attemptDetails.length === 0) return null;
      let totalPenalty = 0;
      let totalAttempt = 0;
      let questionSolved = false;

      for (let i = 0; i < attemptDetails.length; i++) {
        const item = attemptDetails[i];
        const content = item.content;
        if (content?.isCorrect) {
          totalAttempt += 1;
          questionSolved = true;
          setSelectedOption(content?.answer);
        }

        if (!content?.isCorrect) {
          totalAttempt += 1;
          totalPenalty += content?.xpPenalty || 0;
        }
      }

      for (let i = 0; i < helperInteractions.length; i++) {
        const item = helperInteractions[i];
        const content = item.content;

        if (!content?.isCorrect) {
          totalPenalty += content?.xpPenalty || 0;
        }
      }

      if (questionSolved) setShowNextQuestionBtn(true);

      setAttemptCount(totalAttempt);
      setRemainingXP(remainingXP - totalPenalty);
    } catch (error) {
      console.error("Error in (calculateCurrentAttemptProgress): ", error);
      return null;
    }
  };

  /**
   * Begins the panel resizing process.
   * Sets up event listeners for mouse and touch movements to handle the resizing action.
   * Prevents the default behavior of the triggering event.
   *
   * @param {Event} e - The event object (mouse or touch event) that triggered the resize
   * @returns {void}
   */
  const startResizing = (e) => {
    e.preventDefault();
    document.addEventListener("mousemove", resizePanelMouse);
    document.addEventListener("mouseup", stopResizing);

    document.addEventListener("touchmove", resizePanelTouch, {
      passive: false,
    });
    document.addEventListener("touchend", stopResizing);
  };

  const resizePanelMouse = (e) => {
    handleResize(e.clientX, e.clientY);
  };

  const resizePanelTouch = (e) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleResize(touch.clientX, touch.clientY);
    }
  };

  const handleResize = (clientX, clientY) => {
    if (!chartWrapperRef.current) return;

    if (animationFrameId.current) return; // skip if frame already scheduled

    animationFrameId.current = requestAnimationFrame(() => {
      animationFrameId.current = null;

      const newWidth = Math.max(
        window.innerWidth <= 800 ? 50 : 25,
        Math.min(
          ((window.innerWidth - clientX) / window.innerWidth) * 100,
          window.innerWidth <= 800 ? 90 : 43
        )
      );
      const newHeight = Math.max(
        20,
        Math.min(
          ((window.innerHeight - clientY) / window.innerHeight) * 100,
          97
        )
      );

      setChartDimensions({ width: newWidth, height: newHeight });
    });
  };

  const stopResizing = () => {
    document.removeEventListener("mousemove", resizePanelMouse);
    document.removeEventListener("mouseup", stopResizing);

    document.removeEventListener("touchmove", resizePanelTouch);
    document.removeEventListener("touchend", stopResizing);
  };

  const toggleChartPanel = () => {
    setChartPanel(!chartPanel);
  };

  const toggleSidePanel = () => {
    setSidePanel(!sidePanel);
  };

  /**
   * Handles the selection of an answer option in the AI quiz.
   *
   * @async
   * @param {string} option - The selected answer option
   * @returns {Promise<void>}
   * @description
   * This function processes user's answer selection by:
   * 1. Disabling further selections while processing
   * 2. Checking if the selected option matches the correct answer
   * 3. Preparing appropriate response data based on whether the answer is correct:
   *    - Correct answers show success message and proceed to next question
   *    - Incorrect answers apply XP penalty and allow another attempt
   * 4. Submitting the answer to the server via userAttempt API
   * 5. If successful, updating the UI with new XP and progress data
   * 6. Updating chart data and chat state with new XP information if applicable
   */
  const handleOptionSelection = async (option) => {
    console.log("Handling Option Selection");
    console.log("Current Question Index: ", questionIndex);
    console.log("Selected Option: ", option);
    console.log("Current Question", pdfAssessmentQuestions[questionIndex]);


    let questions = selectedPdfAssessment.questions;

    if(questions && !Array.isArray(questions) || questions.length === 0) {
      showToast("Failed to submit your answer. Please try again later.", "error");
      return;
    }
    
    let questionData = questions[questionIndex];
    let correctAnswer = questionData.correct_answer;
    let isCorrect = correctAnswer === option;


    let data = {
      answer: option.toString(),
      isCorrect: isCorrect,
      evaluationNeeded: false,
      penaltyReason: null,
      xpPenalty: 0,
      numberOfAttempts: 1, 
      content: {
        answer: option.toString(),
        questionData: questionData,
        questionIndex: questionIndex,
        isCorrect: isCorrect,
      }
    }

    if (isCorrect) {
      let perQuestionMarks = selectedPdfAssessment.totalMarks / selectedPdfAssessment.questions.length;
      console.log("Correct Answer Selected")
      console.log("Per Question Marks: ", perQuestionMarks);
      data.xpEarned = parseInt(perQuestionMarks);
      data.content.xpEarned = parseInt(perQuestionMarks);
      setActiveAssessmentMarks((prev) => prev + parseInt(perQuestionMarks));
    } else {
      console.log("Incorrect Answer Selected");
      data.xpEarned = 0; 
      data.content.xpEarned = 0
    }

    setDisableOptions(true);
    setSelectedOption(option);
    setIsOptionSelected(true);
    if (option === correctAnswer) {
      showToast(`Correct Answer. Go to Next Question`, "success");
      setShowNextQuestionBtn(true);
    } else {
      showToast(`Incorrect Answer. Go to Next Question`, "error");
      setShowNextQuestionBtn(true);
    }

    const response = await userAttempt(aiAssessmentChat.id, null, data);
    console.log("Response from userAttempt: ", response);
    if (response?.status !== 200) {
      setAiAssessmentChatBlocks(prev => prev.concat(response?.data?.data?.aiChatBlock));
      showToast("Sorry, our AI is busy. Please try again", "error");
      return;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.target.blur();
      fetchHelperResponse(e);
    }
  };

  const validateAIChatForm = () => {
    try {
      if (!selectedTopic) {
        setToastMsg("Please select a topic");
        setSeverityClass("error");
        setOpenToast(true);
        return false;
      }
      if (!currentGrade) {
        setToastMsg("Please select a grade");
        setSeverityClass("error");
        setOpenToast(true);
        return false;
      }
      if (!language) {
        setToastMsg("Please select a language");
        setSeverityClass("error");
        setOpenToast(true);
        return false;
      }
      if (!board) {
        setToastMsg("Please select a board");
        setSeverityClass("error");
        setOpenToast(true);
        return false;
      }
      if (!region) {
        setToastMsg("Please select a region");
        setSeverityClass("error");
        setOpenToast(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in (validateAIChatForm): ", error);
      return false;
    }
  };

  /**
   * Creates a new AI chat session.
   *
   * This function performs the following steps:
   * 1. Shows a loader and validates the AI chat form
   * 2. Finds and validates selected grade, board, region, and language objects
   * 3. Constructs data object with configuration parameters
   * 4. Calls API to create AI chat session
   * 5. Updates state with the new chat information
   * 6. Updates chart data to include the new topic
   * 7. Navigates to the newly created chat session
   *
   * @async
   * @function createNewChat
   * @returns {Promise<void>} Nothing
   * @throws {Error} If API call fails or chat creation encounters an error
   */
  const createNewChat = async () => {
    try {
      setShowLoader(true);
      const isValid = await validateAIChatForm();

      if (!isValid) {
        setShowLoader(false);
        return;
      }

      // actual values
      const _grade = gradeList.find((g) => g.id === currentGrade);
      const _boardObj = boardList.find((b) => b.id.toString() === board);
      const _regionObj = regionList.find((r) => r.id.toString() === region);
      const _languageObj = languageList.find(
        (l) => l.id.toString() === language
      );
      if (!_grade || !_boardObj || !_regionObj || !_languageObj) {
        setToastMsg("Please select a valid configuration");
        setSeverityClass("error");
        setOpenToast(true);
        return false;
      }

      const _data = {
        config: {
          grade: _grade.grade_name.toLowerCase(),
          board: _boardObj.name.toLowerCase(),
          region: _regionObj.name.toLowerCase(),
          language: _languageObj.name.toLowerCase(),
          questionType: "single_choice_mcq",
        },
        aiMode: "tutor",
        topic: selectedTopic,
      };

      const response = await createAIChatSession(_data).catch((error) => {
        console.error("Error in creating new chat: ", error);
        showToast(error?.response?.data?.message || "Error in creating new chat", "error");
        return;
      });
      if (response?.status !== 200) return

      const newChat = response?.data?.data?.block;
      if (!newChat) {
        showToast("Can't create new chat", "error");
        return;
      }

      const sessionId = newChat?.id;
      const newTopic = newChat?.topic?.replaceAll("_", " ");
      setChats((prev) => [
        {
          ...newChat,
          topic: newTopic,
          totalXP: 0,
          level: 1,
          xpToReachNextLevel: 100,
          xpInLevel: 0,
          questionCount: 0,
        },
        ...prev,
      ]);

      setData((prev) => {
        const updatedData = { ...prev };
        if (updatedData?.labels?.length > 0) {
          updatedData.labels.push(newTopic);
          updatedData.datasets[0].data.push(0);
          return updatedData;
        } else {
          return {
            labels: [newTopic],
            datasets: [
              {
                label: "XP",
                data: [0],
                backgroundColor: "rgba(140, 199, 59, 0.2)",
                borderColor: "rgba(140, 199, 59, 1)",
                borderWidth: 1,
                pointBackgroundColor: "rgba(140, 199, 59, 1)",
                fill: false,
              },
            ],
          };
        }
      });

      if (window.innerWidth <= 800) {
        setSidePanel(false);
      }

      handleCloseConfigurationModal();
      navigate(`/ai-skill-journey/${sessionId}`);
    } catch (error) {
      console.error("Error in (createNewChat): ", error);
      showToast(`Error in creating new chat. ${error?.message || ''}`, "error");
    } finally {
      setShowLoader(false);
    }
  };

  const fetchAllChats = async () => {
    try {
      setSidePanelLoader(true);

      const listPdfAssessmentsResponse = await listPdfAssessment(pdfId);
      console.log("listPdfAssessmentsResponse: ", listPdfAssessmentsResponse);
      const pdfAssessments = listPdfAssessmentsResponse?.data?.data?.assessments;
      if (!pdfAssessments || pdfAssessments.length === 0) return;
      setPdfAssessments(pdfAssessments);
    } catch (error) {
      console.error("Error in (fetchAllChats): ", error);
      setToastMsg("Error in fetching AI chats");
      setSeverityClass("error");
    } finally {
      setSidePanelLoader(false);
    }
  };

  // fetch user's grade
  const getGrade = async (_userProfileDetailsID) => {
    try {
      setShowLoader(true);
      let gradeDataQuery = await fetchItemsNextToken({
        query: listTblGradeMasters,
        variables: { filter: { status: { eq: 1 } } },
      });
      if (!gradeDataQuery) throw Error("Error in fetching grade master table");

      let getGradeData = gradeDataQuery?.filter(
        (item) => item._deleted !== true
      );
      if (!getGradeData.length)
        console.error("grade master table: ", getGradeData);

      setGradeList(getGradeData.sort((a, b) => a.series - b.series));
      let educationCard = await fetchSearchItemsNextToken({
        query: searchCards,
        variables: {
          filter: {
            userprofiledetailsID: { eq: _userProfileDetailsID },
            title: { eq: "education" },
          },
        },
      });
      if (!educationCard.length) return;

      let educationCardItems = await fetchSearchItemsNextToken({
        query: searchCardItems,
        variables: {
          filter: { cardID: { eq: educationCard[0].id }, status: { eq: 1 } },
          limit: 500,
        },
      });
      if (!educationCardItems.length) return;

      let sortedEducationCardItems = educationCardItems.sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt)
      );
      let currentEducationCardItem = sortedEducationCardItems.filter(
        (item) => JSON.parse(item.newdata).currentlyStudying === true
      );

      let gradeData = null;
      let gradeListData = null;
      if (!currentEducationCardItem.length) {
        gradeData = JSON.parse(sortedEducationCardItems[0].newdata);
      } else {
        gradeData = JSON.parse(currentEducationCardItem[0].newdata);
      }
      gradeListData = getGradeData.filter(
        (item) => item.id == gradeData.endGrade
      );

      setCurrentGrade(gradeListData[0].id);
    } catch (error) {
      console.error("Error in (getGrade): ", error);
      showToast("Error in fetching grade", "error");
    } finally {
      setShowLoader(false);
    }
  };

  // Fetch chat data
  const getChatData = async (id, questionIndex = null) => {
    try {
      setShowLoader(true);

      if (!id) return;
      const response = await fetchAIChatBlocks(id, questionIndex);
      if (response?.status !== 200) throw Error("Error in fetching AI chat");

      const chatSession = response?.data?.data?.block;
      if (!chatSession) throw Error("No chat session found");

      const questionBlock = chatSession.find(
        (block) => block.type === "generatedQuestion"
      );
      if (!questionBlock) {
        await fetchQuestion();
        return;
      }
      setQuestionData(questionBlock);

      //attempt details
      const attemptDetails = chatSession.filter(
        (block) => block.type === "userAttempt"
      );

      //helper content
      const helperContent = chatSession.filter(
        (block) =>
          block.type === "helperPrompt" || block.type === "helperResponse"
      );
      calculateCurrentAttemptProgress(attemptDetails, helperContent);
      setMessages([
        ...helperContent.map((item) => ({
          role: item.actor === "user" ? "user" : "model",
          content: item.content.content,
        })),
      ]);

    } catch (error) {
      console.error("Error in (getChatData): ", error);
      showToast("Error in fetching chat data", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleDeleteChat = async (id) => {
    try {
      setShowLoader(true);
      if (!id) return;
      const response = await deleteAIChatSession(id);
      if (response?.status !== 200) throw Error("Error in deleting AI chat");

      const deletedChat = response?.data?.data?.aiChat;
      if (!deletedChat) throw Error("No chat session found");

      showToast("Journey deleted successfully", "success");
      const updatedChats = chats.filter((chat) => chat.id !== deletedChat.id);
      setChats(updatedChats);

      setData((prev) => {
        const updatedData = { ...prev };
        const index = updatedData.labels.indexOf(
          deletedChat.topic.replaceAll("_", " ")
        );
        if (index !== -1) {
          updatedData.labels.splice(index, 1);
          updatedData.datasets[0].data.splice(index, 1);
        }
        return updatedData;
      });

      if (pdfId === deletedChat.id) {
        setQuestionIndex(0);
        setMessages([]);
        setCurrentQuestion(null);
        setOptions([]);
        setCorrectAnswer("");
        setSelectedOption("");
        setDisableOptions(false);
        setSidePanel(false);
        navigate("/ai-skill-journey");
        return;
      }
    } catch (error) {
      console.error("Error in (handleDeleteChat): ", error);
      showToast("Error in deleting chat", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const getNewData = async () => {
    let userDetailsID = localStorage.getItem("userDetailsID");
    await dispatch(getAllInterestsAction());
    await dispatch(getUserInterestsAction(userDetailsID));
    await dispatch(getUserSubInterestsAction(userDetailsID));
    setShowPopup(false);
  };

  const showToast = (message, severity = "info") => {
    setOpenToast(false);

    setTimeout(() => {
      setToastMsg(message);
      setSeverityClass(severity);
      setOpenToast(true);
    }, 100); 
  };

  useEffect(() => {
    const init = async () => {
      await dispatch(
        getUserSubInterestsAction(localStorage.getItem("userDetailsID"))
      );
      await dispatch(getAllInterestsAction()); 
      await dispatch(getUserInterestsAction(localStorage.getItem("userDetailsID")))
      await getGrade(localStorage.getItem("userDetailsID"));
      // if (pdfId) {
      //   await getChatData(pdfId);
      // }
      const userDetailsData = await fetchSearchItemsNextToken({
        query: queries.searchUserProfileDetails,
        variables: {
          filter: { id: { eq: localStorage.getItem("userDetailsID") } },
        },
      });
      if (userDetailsData && userDetailsData.length > 0) {
        console.log("User Details Data: ", userDetailsData);
        // setProfile(userDetailsData[0]);
        setFirstName(userDetailsData[0].first_name);
        setLastName(userDetailsData[0].last_name);
      }
      await fetchAllChats();
    };
    init();
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    const canvas = chart?.canvas;

    const handleClick = (event) => {
      const points = chart.getElementsAtEventForMode(
        event,
        "nearest",
        { intersect: false },
        false
      );
      if (points.length > 0) {
        chart.tooltip.setActiveElements(points, { x: event.x, y: event.y });
        chart.update();
      }
    };

    canvas?.addEventListener("click", handleClick);
    return () => canvas?.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    if (!historyRef.current) {
      return;
    }

    setTimeout(() => {
      historyRef?.current?.scrollTo({
        top: historyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 50);
  }, [containerScrollDown]);

  useEffect(() => {
    if (generatingResponse && historyRef.current) {
      historyRef.current.scrollTo({
        top: historyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [generatingResponse]);

  useEffect(() => {
    if (profile.userSubInterests) {
      setSidePanelLoader(false);
    }
  }, [profile.userSubInterests]);

  // useEffect(() => {
  //   if (pdfId) {
  //     setShowLoader(true);
  //     setLatestQuestionIndex(0);
  //     getChatData(pdfId);
  //   }
  // }, [location.pathname]);

  return (
    <div className={styles.ai_demo_wrapper}>
      <Header />
      {showLoader && <Loader2 />}
      {showPopup === true && (
        <InterestsPop openPopup={showPopup} showCancelBtn={true} onClosePopup={(data)=>getNewData()} />
      )}

      <Snackbar
        open={openToast}
        autoHideDuration={3500}
        onClose={handleCloseToast}
      >
        <Alert
          variant="filled"
          onClose={handleCloseToast}
          severity={severityClass}
          sx={{ width: "100%" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>

      {/* Generate Assessment Modal */}
      <CustomModal
        open={openAssessmentGenerateModal}
        handleClose={handleCloseGenerateModal}
      >
        <h3
          style={{
            color: "#04a5a6",
          }}
        >
          Generate Assessment
        </h3>
        <div
          className="modal teal generate_assessment_modal"
          style={{ margin: "20px 0" }}
        >
          <div className="input_row">
            <div className="input_wrapper">
              <label>Selected File</label>
              <div className="selected_file">
                <DescriptionOutlined />
                <span>{selectedFile?.name || "No File Selected"}</span>
              </div>
            </div>
            <div className="input_wrapper">
              <label>Assessment Type</label>
              <select
                className="input_text select"
                value={assessmentType}
                onChange={(e) => setAssessmentType(e.target.value)}
              >
                <option value="default">Select Assessment Type</option>
                <option value={0}>MCQ</option>
                <option value={1}>Fill in the Blanks</option>
                <option value={2}>True or False</option>
                <option value={5}>Descriptive</option>
              </select>
            </div>
          </div>

          <div className="input_row">
            <div className="input_wrapper">
              <label>Assessment Name</label>
              <input
                type="text"
                className="input_text"
                placeholder="Maths - Know your Numbers - level 1"
                value={assessmentName}
                onChange={(e) => setAssessmentName(e.target.value)}
              />
            </div>
            <div className="input_wrapper">
              <label>Assessment Description</label>
              <input
                type="text"
                className="input_text"
                placeholder="Structured Assessment for level 1"
                value={assessmentDescription}
                onChange={(e) => setAssessmentDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="input_row">
            <div className="input_wrapper">
              <label>Number Of Questions</label>
              <select
                className="input_text select"
                value={numberOfQuestions}
                onChange={(e) => setNumberOfQuestions(e.target.value)}
              >
                <option value="default">Select Number of Questions</option>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </div>
            <div className="input_wrapper">
              <label>Duration</label>
              <select
                className="input_text select"
                value={assessmentDuration}
                onChange={(e) => setAssessmentDuration(e.target.value)}
              >
                <option value="default">Select Duration</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={180}>3 hours</option>
              </select>
            </div>
          </div>

          <div className="input_row">
            <div className="input_wrapper">
              <label>Total Marks</label>
              <input
                type="number"
                className="input_text"
                placeholder="Total Marks"
                value={totalMarks}
                onChange={(e) => setTotalMarks(e.target.value)}
              />
            </div>
            <div className="input_wrapper">
              <label>Passing Marks</label>
              <input
                type="number"
                className="input_text"
                placeholder="Passing Marks"
                value={passingMarks}
                onChange={(e) => setPassingMarks(e.target.value)}
              />
            </div>
          </div>

          <div className="input_wrapper">
            <label>Prompt</label>
            <textarea
              className="input_text"
              placeholder="Enter your prompt here"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            ></textarea>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <button
            style={{
              backgroundColor: "#04a5a6",
              color: "white",
              padding: "5px 7px",
              border: "none",
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={handleCloseGenerateModal}
          >
            <span>Cancel</span>
            <CloseOutlined style={{ fontSize: "16px" }} />
          </button>
          <button
            style={{
              backgroundColor: "#04a5a6",
              color: "white",
              padding: "5px 7px",
              border: "none",
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={handleGenerateAssessment}
          >
            <span>Generate</span>
            <AutoAwesomeOutlined style={{ fontSize: "16px" }} />
          </button>
        </div>
      </CustomModal>

      {/* View Assessment Modal */}
      <CustomModal
        open={viewAssessmentQuestionsModal}
        handleClose={() => setViewAssessmentQuestionsModal(false)}
      >
        <h3
          style={{
            color: "#8cc63c",
          }}
        >
          {selectedAssessmentForModal?.name}
        </h3>
        <div className="modal teal generate_assessment_modal" style={{ margin: "20px 0" }}>
          {selectedAssessmentForModal?.questions?.map((question, index) => (
            <div className="mcq_question_block" key={index}>
              <p className="question">{index + 1}. {question.question}</p>
              <div className="options">
                {question.options.map((option, index) => (
                  <div className="option" key={index}>
                    <span>{index + 1}.</span>
                    {option}
                  </div>
                ))}
              </div>
              <div className="answer">
                <span>Correct Answer :</span>
                {question.options[question.correct_answer]}
              </div>
            </div>
          ))}

        </div>
        <div
          style={{
            width: "100%",
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <button
            style={{
              backgroundColor: "#04a5a6",
              color: "white",
              padding: "5px 7px",
              border: "none",
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={() => setOpenAssessmentEditModal(false)}
          >
            <span>Close</span>
            <CloseOutlined style={{ fontSize: "16px" }} />
          </button>
        </div>
      </CustomModal>

      <Box className={styles.ai_body}>
        <div className={`${styles.side_panel} ${!sidePanel && styles.close}`}>
          <div
            className={`${styles.collapse_btn} ${!sidePanel && styles.close}`}
            onClick={toggleSidePanel}
          >
            <ArrowLeft className={styles.icon} />
          </div>

          <div className={`${styles.header} ${!sidePanel && styles.close}`}>
            <p className={styles.title}>Your PDF Assessments</p>
            {/* Not Showing Now Since Modal not working */}
            {/* <button
              className={styles.btn}
              // onClick={() => setOpenConfigurationModal(true)}
              onClick={async () => {
                console.log("Generate New Assessment clicked");
                // navigate(`/ai-assessment-dashboard`);
                // add modal here to generate new assessment
                setOpenAssessmentGenerateModal(true);
              }}
            >
              <AddOutlined style={{ fontSize: "0.8rem" }} />
              <span>Generate New Assessment</span>
            </button> */}
          </div>

          {/* Repeat this div if needed */}
          <div className={`${styles.body} ${!sidePanel && styles.close}`}>
            <div className={styles.divider} />
            <div className={styles.header}>Assessments Progress</div>

            {sidePanelLoader ? (
              <Loading loadingPrimary="" loadingSecondary="loading..." />
            ) : (
              <div className={styles.chat_list}>
                {pdfAssessments?.length > 0 ? (
                  pdfAssessments.map((pdfAssessment) => {
                    const pdfid = pdfAssessment.pdfId;
                    // const isSelected = pdfid === pdfId;
                    const isSelected = false;
                    return (
                      <div
                        className={`${styles.chat_item} ${
                          isSelected && styles.active
                        }`}
                        key={pdfAssessment.aiCardItemid}
                      >
                        <div className={styles.header}>
                          <div className={`${styles.content}`}>
                            {/* <p className={styles.level}>{chat.level}</p> */}
                            <p className={styles.topic} title={pdfAssessment.name}>
                              {pdfAssessment.name}
                            </p>
                          </div>
                          
                          {/* View Assessment Questions */}
                          <div
                            className={styles.actions}
                            title="See Questions"
                            onClick={async (e) => {
                              console.log("View Questions clicked for: ", pdfAssessment.name);
                              e.stopPropagation();
                              setSelectedAssessmentForModal(pdfAssessment);
                              setViewAssessmentQuestionsModal(true);
                            }}
                          >
                            <VisibilityOutlined className={styles.delete} />
                          </div>
                          {/* Delete Assessment */}
                          <div
                            className={styles.actions}
                            title="Delete Assessment"
                            onClick={async (e) => {
                              console.log("Delete clicked for: ", pdfAssessment.name);
                              e.stopPropagation();
                              // call api to delete assessment
                              const response = await deleteAssessment(pdfAssessment.aiCardItemid)
                              if (response?.status === 200) {
                                showToast("Assessment deleted successfully", "success");
                                setPdfAssessments((prev) =>
                                  prev.filter((assessment) => assessment.aiCardItemid !== pdfAssessment.aiCardItemid)
                                );
                              } else {
                                showToast("Error in deleting assessment", "error");
                              }
                            }}
                          >
                            <DeleteOutline className={styles.delete} />
                          </div>
                          {/* Take Assessment */}
                          <div
                            className={styles.actions}
                            title="Take Assessment"
                            onClick={async (e) => {
                              setShowLoader(true);
                              console.log("Loder Set to True");
                              e.stopPropagation();
                              console.log("Click Propagation was Stopped");
                              setPdfAssessmentQuestions(pdfAssessment.questions)
                              console.log("PDF assessment questions set");
                              setSelectedPdfAssessment(pdfAssessment);
                              console.log("Selected PDF Assessment set");
                            }}
                          >
                            <ArrowForwardIosOutlined className={styles.delete} />
                          </div>
                        </div>

                        <div className={styles.content}>
                          <div className={styles.config}>
                            <p>Type: {pdfAssessment.type}</p>
                            <p>Duration: {pdfAssessment.duration}</p>
                            <p>Question Count: {pdfAssessment.questions.length}</p>
                            <p>Total Marks: {pdfAssessment.totalMarks}</p>
                            <p>Passing Marks: {pdfAssessment.passingMarks}</p>
                            {/* <p>
                              Language:
                              {" " + chat?.config?.language?.toUpperCase() ||
                                "-"}
                            </p> */}
                          </div>

                          {/* <div className={styles.xp_progress}>
                            {`${chat.totalXP} / ${chat.xpToReachNextLevel} XP`}
                          </div> */}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className={styles.no_progress}>
                    No Assessments Found
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {!showLoader && (
          <div className={styles.main_panel}>
            {currentQuestion === null && (
              <div className={styles.info_message}>
                <p className={styles.header}>Take Your Assessments</p>
                <div className={styles.text}>
                  Take your assessments to test your knowledge and skills. You can also shre this assessment with your friends and family to challenge them.
                  Select an assessment from the side panel to get started.
                </div>
                {/* <button
                  className={styles.btn}
                  onClick={() => setOpenConfigurationModal(true)}
                >
                  Ready to learn? Let's Go
                </button> */}
              </div>
            )}
            {currentQuestion && (
              <>
                {/* Tutor Chat */}
                <div className={styles.question_option_panel}>
                  <div
                    className={`${styles.question_panel} ${
                      !sidePanel && styles.side_panel_close
                    }`}
                  >
                    <div className={styles.header}>
                      <span className={styles.title}>Question</span>
                      <div className={styles.actions}>
                        {/* <ArrowLeft
                          className={`${styles.action_btn} ${
                            questionIndex === 0 && styles.disable
                          }`}
                          onClick={async () => {
                            if (questionIndex === 0) return;
                            let prevQuestionIndex = questionIndex - 1;
                            await getChatData(pdfId, prevQuestionIndex);
                            setQuestionIndex(prevQuestionIndex);
                          }}
                        /> */}
                        <span className={styles.question_index}>
                          {questionIndex + 1}
                        </span>
                        {/* <ArrowLeft
                          className={`${styles.action_btn} ${styles.left} ${
                            questionIndex === latestQuestionIndex &&
                            styles.disable
                          }`}
                          onClick={async () => {
                            if (questionIndex === latestQuestionIndex) return;
                            let nextQuestionIndex = questionIndex + 1;
                            await getChatData(pdfId, nextQuestionIndex);
                            setQuestionIndex(nextQuestionIndex);
                          }}
                        /> */}
                      </div>
                    </div>

                    {questionPanelLoader ? (
                      <Loading
                        loadingPrimary=""
                        loadingSecondary="loading..."
                      />
                    ) : (
                      <div className={styles.question}>
                        {currentQuestion?.length > 0 ? (
                          <>
                            <div>{currentQuestion}</div>
                            {showNextQuestionBtn && (
                              <div
                                className={styles.btn}
                                onClick={async () => await fetchQuestion()}
                              >
                                Next Question
                              </div>
                            )}
                          </>
                        ) : (
                          "Please Select an Interest from the side panel"
                        )}
                      </div>
                    )}
                  </div>

                  {!questionPanelLoader && (
                    <div className={styles.option_panel}>
                      {options?.length > 0 &&
                        options.map((item, index) => (
                          <label
                            key={item}
                            className={`${styles.option} ${
                              selectedOption === index && styles.selected
                            } ${
                              selectedOption === correctAnswer
                                ? styles.correct
                                : styles.incorrect
                            }`}
                          >
                            <input
                              className={styles.hidden_input}
                              type="radio"
                              name="mcq"
                              value={item}
                              checked={selectedOption === index}
                              onChange={() => handleOptionSelection(index)}
                              disabled={disableOptions}
                            />
                            {item}
                          </label>
                        ))}
                    </div>
                  )}
                </div>
                {/* Helper Chat */}
                <div
                  className={`${styles.chat_panel} ${chatPanel && styles.open}`}
                >
                  {/* For Mobile and Tablet Screens */}
                  {window.innerWidth <= 800 && (
                    <div
                      className={styles.collapse_btn}
                      onClick={() => setChatPanel(!chatPanel)}
                    >
                      <AutoAwesomeOutlined className={styles.icon} />
                      <span>Need Help?</span>
                    </div>
                  )}

                  <div
                    className={styles.messages_panel}
                    ref={historyRef}
                  >
                    <div
                      className={styles.info_message}
                    >
                      {showBarGraph && (
                        <Bar
                          // style={{ height: "400px !important" }}   
                          className={styles.chart}
                          options={barGraphOptionsState} 
                          data={barGraphDataState}
                          height={300} 
                        />
                      )}
                    </div>
                  </div>

                  {/* Helper Prompt Input Text Box */}
                  {/* <div className={styles.prompt_container}>
                    <div className={styles.wrapper}>
                      <textarea
                        className={styles.prompt_input}
                        type="text"
                        placeholder="Enter your message"
                        value={userPrompt}
                        onChange={(e) => setUserPrompt(e.target.value)}
                        onKeyDown={handleKeyDown}
                      />
                      <button
                        className={styles.send_btn}
                        type="submit"
                        disabled={userPrompt.trim() === ""}
                        onClick={(e) => fetchHelperResponse(e)}
                      >
                        <ArrowUpwardOutlined className={styles.icon} />
                      </button>
                    </div>
                  </div> */}
                </div>
              </>
            )}
          </div>
        )}

      </Box>
    </div>
  );
};

export default AIAssessments;
